import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { fetchFeatures } from "../utils/fetchFeatures";
import LeadFieldPermission from "./LeadFieldPermission";
import SaveIcon from "@mui/icons-material/Save";
import ButtonBack from "../utils/ButtonBack";
import { useOrganization } from "../utils/OrganizationContext";
import ThemedSelect from "../utils/ThemedSelect";
import PlanUpgradeDialog from "../utils/PlanUpgradeDialog";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const lbl = { display: "block", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: "#3D4C60" };
const inp = (readOnly) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 42,
  padding: "0 12px",
  margin: "0 0 14px",
  background: readOnly ? "#F7F9FC" : "#FFFFFF",
  border: `1px solid ${FIELD_EDGE}`,
  borderRadius: 10,
  fontFamily: readOnly ? MONO : FONT,
  fontSize: readOnly ? 13 : 14,
  color: readOnly ? "#5A6779" : INK,
  outline: "none",
});
const card = (locked) => ({
  width: "100%",
  boxSizing: "border-box",
  margin: 0,
  padding: "18px 20px",
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  boxShadow: "none",
  opacity: locked ? 0.5 : 1,
  pointerEvents: locked ? "none" : "auto",
});
const h2 = { fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK };
const h3 = { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: MUTED };
const primaryBtn = {
  height: 42,
  px: 2.5,
  margin: "0 !important",
  gap: 0.75,
  borderRadius: "10px !important",
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: "#084767 !important" },
  "&.Mui-disabled": { opacity: 0.55 },
};
const chip = (on, locked) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  boxSizing: "border-box",
  minHeight: 42,
  margin: 0,
  padding: "8px 12px",
  background: on ? (locked ? "#F1F4F8" : "#EAF2FB") : "#FFFFFF",
  border: `1px solid ${on ? (locked ? LINE : "#9FC3EA") : LINE}`,
  borderRadius: 10,
  cursor: locked ? "not-allowed" : "pointer",
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: on ? 600 : 500,
  color: on ? INK : "#4A5A6E",
  textAlign: "left",
  lineHeight: 1.3,
});
const box = (on, locked) => ({
  flex: "0 0 16px",
  width: 16,
  height: 16,
  boxSizing: "border-box",
  borderRadius: 5,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 800,
  lineHeight: 1,
  color: "#FFFFFF",
  background: on ? (locked ? "#8494A8" : PRIMARY) : "#FFFFFF",
  border: `1.5px solid ${on ? (locked ? "#8494A8" : PRIMARY) : "#C3CDD9"}`,
});

const STATIC_FIELDS = [
  { name: "name", label: "Lead Name", type: "text" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "alternatePhone", label: "Alternate Phone", type: "text" },
  { name: "isWhatsapp", label: "Is WhatsApp", type: "boolean" },
  { name: "isAlternateWhatsapp", label: "Alternate WhatsApp", type: "boolean" },

  { name: "email", label: "Email", type: "email" },
  { name: "leadSource", label: "Lead Source", type: "text" },

  { name: "currentCity", label: "Current City", type: "text" },
  { name: "currentCountry", label: "Current Country", type: "text" },

  { name: "requirement", label: "Requirement", type: "text" },

  {
    name: "preferredPropertyType",
    label: "Preferred Property Type",
    type: "text",
  },

  { name: "minBudget", label: "Minimum Budget", type: "number" },
  { name: "maxBudget", label: "Maximum Budget", type: "number" },

  { name: "timelineToBuy", label: "Timeline To Buy", type: "text" },
  { name: "purpose", label: "Purpose", type: "text" },

  // DATE FIELDS
  { name: "nextFollowUp", label: "Next Follow Up", type: "date" },
  { name: "lastFollowUp", label: "Last Follow Up", type: "date" },
  { name: "remark", label: "Comment", type: "text" },

  // UTM FIELDS
  // { name: "utmSource", label: "UTM Source", type: "text" },
  // { name: "utmMedium", label: "UTM Medium", type: "text" },
  // { name: "utmCampaign", label: "UTM Campaign", type: "text" },
  // { name: "utmTerm", label: "UTM Term", type: "text" },
  // { name: "utmContent", label: "UTM Content", type: "text" },

  { name: "leadScore", label: "Lead Score", type: "number" },
  { name: "aiScore", label: "AI Score", type: "number" },
];

const FIELD_TYPES = [
  "TEXT",
  "NUMBER",
  "DATE",
  "BOOLEAN",
  "SINGLE_SELECT",
  "MULTI_SELECT",
];

const CreateStructure = () => {
  const storedUser = JSON.parse(localStorage.getItem("user"));

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(
    JSON.parse(localStorage.getItem("projectId")) || "",
  );
  const [structure, setStructure] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const mandatory = [
    "name",
    "phone",
    "alternatePhone",
    "isWhatsapp",
    "isAlternateWhatsapp",
    "email",
    "nextFollowUp",
    "lastFollowUp",
  ];
  const [selectedFields, setSelectedFields] = useState(mandatory);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [fieldForm, setFieldForm] = useState({
    fieldName: "",
    label: "",
    type: "TEXT",
    options: [],
  });

  const [optionInput, setOptionInput] = useState("");
  // Plan doesn't include this feature — show an in-page upgrade panel
  // instead of an alert + redirect.
  const [noAccess, setNoAccess] = useState(false);
  // BASIC can build a structure but not custom fields (PRO). null = still checking.
  const [customAllowed, setCustomAllowed] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("LEAD_STRUCTURE");
  const openUpgrade = (feature) => {
    setUpgradeFeature(feature);
    setUpgradeOpen(true);
  };
  const navigate = useNavigate();
  const subs = JSON.parse(localStorage.getItem("subs"));
  const orgId = JSON.parse(localStorage.getItem("org"));
  const { role } = useOrganization();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const hasAccess = await fetchFeatures(
          "LEAD_STRUCTURE",
          subs,
          storedUser.accessToken,
        );

        if (!hasAccess) {
          setNoAccess(true);
          return;
        }

        fetchFeatures("LEAD_CUSTOM_FIELD", subs, storedUser.accessToken)
          .then((ok) => setCustomAllowed(!!ok))
          .catch(() => setCustomAllowed(true));

        // const decoded = jwtDecode(storedUser.accessToken);
        role === "ADMIN" ? fetchProjectsByAdmin() : fetchProjects();
      } catch (err) {
        console.error(err);
      }
    };

    if (subs) checkAccess();
    else {
      setNoAccess(true);
    }
  }, [orgId]);

  const fetchProjectsByAdmin = async () => {
    await axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      })
      .then((res) => setProjects(res.data));
  };

  const fetchProjects = async () => {
    await axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      )
      .then((res) => setProjects(res.data));
  };

  useEffect(() => {
    if (!projectId) return;

    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/custom-field?projectId=${projectId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      )
      .then((res) => setCustomFields(res.data));

    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/lead-structure/exists?projectId=${projectId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      )
      .then((res) => setStructure(res.data));
  }, [projectId]);

  const addCustomField = async () => {
    try {
      const payload = {
        projectId,
        ...fieldForm,
        fieldName: fieldForm.label.trim().toLowerCase().replace(/\s+/g, "_"),
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/custom-field`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      setFieldForm({ fieldName: "", label: "", type: "TEXT", options: [] });
      setOptionInput("");

      // refresh
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/custom-field?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setCustomFields(res.data);
    } catch (error) {
      console.log(error);
      alert(error.response.data.Exception);
    }
  };

  const addOption = () => {
    if (!optionInput.trim()) return;
    setFieldForm({
      ...fieldForm,
      options: [...fieldForm.options, { value: optionInput }],
    });
    setOptionInput("");
  };

  const submitStructure = async () => {
    try {
      setLoading(true);
      const STATIC_FIELD_NAMES = new Set(STATIC_FIELDS.map((f) => f.name));
      const finalFields = Array.from(
        new Set([...mandatory, ...selectedFields]),
      );

      const payload = finalFields.map((fieldName) => ({
        projectId,
        fieldName,
        fieldType: STATIC_FIELD_NAMES.has(fieldName) ? "STATIC" : "CUSTOM",
      }));

      if (structure) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/lead-structure`,
          payload,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/lead-structure`,
          payload,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
      }

      alert("Lead structure saved");
      setStep(2);
    } catch (error) {
      alert(error.response.data.Exception);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (role === "ADMIN") navigate(`/dashboard/edit-structure/${projectId}`);
    else navigate(`/dashboard/edit-structure/${projectId}`);
  };

  const lockedNote = (
    <Typography
      variant="body2"
      mb={1.25}
      sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 500, color: "#8A5A08", background: "#FDF4E1", border: "1px solid #F3E0B4", borderRadius: "10px", p: "8px 12px" }}
    >
      Lead structure already created for this project. Use "Edit
      Structure" above to make changes.
    </Typography>
  );

  if (noAccess) {
    return (
      <div style={{ fontFamily: FONT, color: INK, textAlign: "left" }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK, mb: 2 }}>
          Lead Structure
        </Typography>
        <Box
          sx={{
            maxWidth: 520,
            p: "24px 22px 22px",
            background: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: "16px",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              mb: 1.5,
              borderRadius: "999px",
              background: "#EFEDFB",
              border: "1px solid #D3D0F2",
              color: "#4A3FBF",
              fontFamily: FONT,
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M2.4 7.1a1.1 1.1 0 0 1 1.72-.9l3.1 2.2L10.9 3.6a1.3 1.3 0 0 1 2.2 0l3.68 4.8 3.1-2.2a1.1 1.1 0 0 1 1.72.9L19.9 17.3a1 1 0 0 1-.98.8H5.08a1 1 0 0 1-.98-.8L2.4 7.1zM5 19.6h14a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2z"/></svg>
            Paid plan
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}>
            Upgrade your plan to access this feature.
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, lineHeight: 1.5, color: "#4A5A6E", mt: 0.75, mb: 2.5 }}>
            Lead Structure lets you choose which fields each project's lead form captures and which roles can see them.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          <Button
            onClick={() => openUpgrade("LEAD_STRUCTURE")}
            sx={{
              height: 40,
              px: 2,
              borderRadius: "10px",
              background: "#4A3FBF",
              color: "#FFFFFF",
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: "#3D33A3" },
            }}
          >
            See plans
          </Button>
          <Button
            onClick={() => navigate("/dashboard")}
            sx={{
              height: 40,
              px: 2,
              borderRadius: "10px",
              border: `1px solid ${FIELD_EDGE}`,
              background: "#FFFFFF",
              color: INK,
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#F4F7FA" },
            }}
          >
            Back to dashboard
          </Button>
          </Box>
        </Box>
        <PlanUpgradeDialog
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          featureKey={upgradeFeature}
          reason="Lead Structure is part of a paid plan."
        />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT, color: INK, textAlign: "left" }}>
      <PlanUpgradeDialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        featureKey={upgradeFeature}
        reason="Custom fields are part of the Pro plan."
      />
      {step === 1 && (
        <div>
          <Typography sx={{ fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK, mb: 0.5 }}>
            Lead Structure
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: "#4A5A6E", mb: 2 }}>
            Step 1 of 2 · Choose which fields this project's lead form captures.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              p: "12px 14px",
              mb: 2,
              background: "#FFFFFF",
              border: `1px solid ${LINE}`,
              borderRadius: "14px",
            }}
          >
            <Box sx={{ flex: "1 1 280px", minWidth: 0, maxWidth: { sm: 420 } }}>
              <label style={lbl}>Select Project</label>
              <Autocomplete
                name="projectId"
                options={projects || []}
                getOptionLabel={(option) => option.projectName || ""}
                value={projects?.find((p) => p.projectId === projectId) || null}
                onChange={(e, value) => {
                  setProjectId(value?.projectId || "");
                  localStorage.setItem(
                    "projectId",
                    JSON.stringify(value?.projectId || ""),
                  );
                }}
                isOptionEqualToValue={(option, value) =>
                  option.projectId === value.projectId
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Project" />
                )}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: "12px",
                      border: `1px solid ${LINE}`,
                      boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
                      "& .MuiAutocomplete-option": { fontFamily: FONT, fontSize: 13.5, borderRadius: "8px", mx: 0.75 },
                    },
                  },
                }}
                sx={{
                  margin: 0,
                  "& .MuiInputBase-root": {
                    minHeight: 42,
                    fontFamily: FONT,
                    fontSize: "14px",
                    fontWeight: 600,
                    borderRadius: "10px",
                    padding: "1px 7px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${PRIMARY} !important`, borderWidth: "1px !important" },
                }}
              />
            </Box>

            {structure && (
              <>
                <p style={{ flex: "1 1 240px", margin: 0, fontFamily: FONT, fontSize: 13, color: "#4A5A6E", textWrap: "pretty" }}>
                  This project already has a lead form structure. You can edit the
                  lead form structure.
                </p>
                <Button
                  className="btn-contained"
                  sx={{ ...primaryBtn, marginTop: "0 !important", alignSelf: "flex-end" }}
                  onClick={handleEdit}
                >
                  Edit Structure
                </Button>
              </>
            )}
          </Box>

          {projectId && (
            <>
              <Typography variant="h5" sx={{ ...h2, mt: 1, mb: 1.25 }}>
                Add Custom Field
              </Typography>
              {customAllowed === false && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1.5,
                    p: "12px 14px",
                    mb: 1.25,
                    background: "#EFEDFB",
                    border: "1px solid #D3D0F2",
                    borderRadius: "12px",
                  }}
                >
                  <span style={{ display: "inline-flex", color: "#4A3FBF" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M2.4 7.1a1.1 1.1 0 0 1 1.72-.9l3.1 2.2L10.9 3.6a1.3 1.3 0 0 1 2.2 0l3.68 4.8 3.1-2.2a1.1 1.1 0 0 1 1.72.9L19.9 17.3a1 1 0 0 1-.98.8H5.08a1 1 0 0 1-.98-.8L2.4 7.1zM5 19.6h14a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2z"/></svg>
                  </span>
                  <p style={{ flex: "1 1 240px", margin: 0, fontFamily: FONT, fontSize: 13, color: "#3D4C60", textWrap: "pretty" }}>
                    <strong style={{ color: "#4A3FBF" }}>Custom fields are a Pro feature.</strong>{" "}
                    Your plan includes the standard fields below.
                  </p>
                  <Button
                    onClick={() => openUpgrade("LEAD_CUSTOM_FIELD")}
                    sx={{ height: 34, px: 1.75, borderRadius: "9px", background: "#4A3FBF", color: "#FFFFFF", fontFamily: FONT, fontSize: 12.5, fontWeight: 700, textTransform: "none", boxShadow: "none", "&:hover": { background: "#3D33A3" } }}
                  >
                    Upgrade to Pro
                  </Button>
                </Box>
              )}
              {structure && lockedNote}
              <div className="add-forms" style={card(structure || customAllowed === false)}>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <label style={lbl}>Label</label>
                    <input
                      placeholder="Label"
                      value={fieldForm.label}
                      onChange={(e) =>
                        setFieldForm({ ...fieldForm, label: e.target.value })
                      }
                      style={inp(false)}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <label style={lbl}>Field Name</label>
                    <input
                      placeholder="Field Name"
                      value={fieldForm.label
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, "_")}
                      readOnly
                      style={inp(true)}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <label style={lbl}>Type</label>
                    <ThemedSelect
                      name="type"
                      value={fieldForm.type}
                      onChange={(e) =>
                        setFieldForm({ ...fieldForm, type: e.target.value })
                      }
                      options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
                      sx={{ mb: 1.75 }}
                    />
                  </Grid>
                </Grid>

                {(fieldForm.type === "SINGLE_SELECT" ||
                  fieldForm.type === "MULTI_SELECT") && (
                  <div style={{ padding: "12px 14px", marginBottom: 14, background: "#F7F9FC", border: `1px solid ${LINE}`, borderRadius: 12 }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <input
                        placeholder="Option value"
                        value={optionInput}
                        onChange={(e) => setOptionInput(e.target.value)}
                        style={{ ...inp(false), flex: "1 1 200px", width: "auto", margin: 0 }}
                      />
                      <Button
                        className="btn-contained"
                        onClick={addOption}
                        sx={{ ...primaryBtn, marginTop: "0 !important", background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important`, "&:hover": { background: "#EAF2FB !important" } }}
                        disabled={structure}
                      >
                        + Add Option
                      </Button>
                    </div>

                    <ul style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", margin: fieldForm.options.length ? "10px 0 0" : 0, padding: 0 }}>
                      {fieldForm.options.map((o, i) => (
                        <li
                          key={i}
                          style={{ padding: "3px 10px", borderRadius: 999, background: "#EAF2FB", border: "1px solid #C9DEF3", fontSize: 12.5, fontWeight: 600, color: "#1B4F82" }}
                        >
                          {o.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                >
                  <Button
                    className="btn-contained"
                    onClick={addCustomField}
                    disabled={structure || customAllowed === false}
                    sx={primaryBtn}
                  >
                    <SaveIcon sx={{ fontSize: 17 }} /> Save Custom
                    Field
                  </Button>
                </Box>
              </div>
            </>
          )}

          {projectId && (
            <>
              <Typography variant="h5" sx={{ ...h2, mt: 3, mb: 1.25 }}>
                Form Structure
              </Typography>
              {structure && lockedNote}
              <div className="add-forms" style={card(structure)}>
                <Typography variant="h6" sx={{ ...h3, mb: 1.25 }}>
                  Static Fields
                </Typography>
                <Grid container spacing={{ xs: 1, sm: 1.25 }}>
                  {STATIC_FIELDS.map((f) => {
                    const isMandatory = mandatory.includes(f.name);
                    const isSelected =
                      isMandatory || selectedFields.includes(f.name);

                    return (
                      <Grid
                        size={{ xs: 6, sm: 3, md: 2 }}
                        key={f.name}
                        textAlign="center"
                      >
                        <label
                          title={
                            isMandatory
                              ? "This field is required and cannot be removed"
                              : ""
                          }
                          className={`project-checkbox ${isSelected ? "checkbox-active" : ""} ${
                            isMandatory ? "checkbox-locked" : ""
                          }`}
                          style={chip(isSelected, isMandatory)}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isMandatory}
                            onChange={() => {
                              if (isMandatory) return;
                              setSelectedFields((prev) =>
                                prev.includes(f.name)
                                  ? prev.filter((x) => x !== f.name)
                                  : [...prev, f.name],
                              );
                            }}
                            style={{ width: "auto", display: "none" }}
                          />
                          <span style={box(isSelected, isMandatory)}>{isSelected ? "✓" : ""}</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            {f.label}
                            {isMandatory && (
                              <span style={{ color: "#C42B24", marginLeft: 3 }}>
                                *
                              </span>
                            )}
                          </span>
                        </label>
                      </Grid>
                    );
                  })}
                </Grid>

                <Typography variant="h6" sx={{ ...h3, mt: 2.5, mb: 1.25 }}>
                  Custom Fields
                </Typography>
                <Grid container spacing={{ xs: 1, sm: 1.25 }}>
                  {customFields.map((f) => (
                    <Grid
                      size={{ xs: 6, sm: 3, md: 2 }}
                      key={f.fieldId}
                      textAlign={"center"}
                    >
                      <label
                        className={`project-checkbox ${
                          selectedFields.includes(f.fieldName)
                            ? "checkbox-active"
                            : ""
                        }`}
                        style={chip(selectedFields.includes(f.fieldName), false)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(f.fieldName)}
                          onChange={() =>
                            setSelectedFields((prev) =>
                              prev.includes(f.fieldName)
                                ? prev.filter((x) => x !== f.fieldName)
                                : [...prev, f.fieldName],
                            )
                          }
                          style={{ width: "auto", display: "none" }}
                        />
                        <span style={box(selectedFields.includes(f.fieldName), false)}>
                          {selectedFields.includes(f.fieldName) ? "✓" : ""}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>{f.label}</span>
                      </label>
                    </Grid>
                  ))}
                </Grid>
              </div>

              <Box
                className="btn-container"
                mb={2}
                sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "flex-end", alignItems: "center", gap: 1.25, mt: 2 }}
              >
                <ButtonBack
                  handleBack={() => navigate("/dashboard/projects")}
                />
                <Button
                  className="btn-contained"
                  onClick={submitStructure}
                  sx={{
                    ...primaryBtn,
                    width: { xs: "100% !important", sm: "auto !important" },
                  }}
                  disabled={loading || structure}
                >
                  {loading ? (
                    "Saving..."
                  ) : (
                    <>
                      <SaveIcon sx={{ fontSize: 17 }} /> Save Lead
                      Form Structure
                    </>
                  )}
                </Button>
              </Box>
              <Box mb={2} sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 1.25, mt: 2 }}>
                <Button
                  className="btn-contained"
                  onClick={submitStructure}
                  fullWidth
                  disabled={loading || structure}
                  sx={{ ...primaryBtn, height: 44 }}
                >
                  {loading ? (
                    "Saving..."
                  ) : (
                    <>
                      <SaveIcon sx={{ fontSize: 17 }} /> Save Lead
                      Form Structure
                    </>
                  )}
                </Button>
                <ButtonBack
                  fullWidth={true}
                  handleBack={() => navigate("/dashboard/projects")}
                />
              </Box>
            </>
          )}
        </div>
      )}
      {step === 2 && <LeadFieldPermission projectId={projectId} />}
    </div>
  );
};

export default CreateStructure;
